import { motion } from "framer-motion";
import { ClipboardCheck, PackageSearch, HardHat, LifeBuoy } from "lucide-react";
import { MARQUEE_TAGS, SUPPORT_CARDS } from "@/lib/data";
import { Reveal } from "@/components/Reveal";

const ICONS = { clipboard: ClipboardCheck, package: PackageSearch, hardhat: HardHat, lifebuoy: LifeBuoy };

function MarqueeBand() {
  const items = [...MARQUEE_TAGS, ...MARQUEE_TAGS];
  return (
    <div className="overflow-hidden py-6 border-y border-medis-silver/20 bg-medis-subtle/40" data-testid="support-marquee">
      <div className="marquee-track items-center gap-14 pr-14">
        {items.map((tag, i) => (
          <div key={i} className="flex items-center gap-3 text-[13px] font-semibold tracking-[0.16em] text-medis-navy/60 whitespace-nowrap" aria-hidden={i >= MARQUEE_TAGS.length}>
            <span className="h-1.5 w-1.5 rounded-full bg-medis-teal" />
            {tag}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Support() {
  return (
    <section id="support" className="py-24 lg:py-28 bg-white" data-testid="support-section">
      <MarqueeBand />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 mt-16">
        <div className="max-w-3xl mb-12">
          <Reveal>
            <div className="text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold">Support & Maintenance</div>
            <h2 className="mt-3 font-display font-bold text-medis-navy text-3xl sm:text-4xl lg:text-[40px] leading-[1.1] tracking-tight">
              A service ecosystem built to protect equipment performance.
            </h2>
            <p className="mt-4 text-medis-gray text-[15px] leading-relaxed max-w-2xl">
              MEDIS keeps support visible as a core advantage behind every sale protecting performance, improving uptime
              and giving healthcare providers dependable technical access.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SUPPORT_CARDS.map((card, i) => {
            const Icon = ICONS[card.icon];
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: Math.min(i * 0.08, 0.3) }}
                className="group relative rounded-xl border border-medis-silver/30 bg-white p-7 overflow-hidden cursor-default hover:shadow-lg transition-all duration-300"
                data-testid={`support-${card.id}`}
              >
                <div className="absolute top-0 left-0 h-[3px] w-0 group-hover:w-full rounded-full transition-all duration-500" style={{ background: card.accent }} />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"
                  style={{ background: `radial-gradient(circle at 30% 20%, ${card.accent}08, transparent 70%)` }}
                />
                <div className="relative inline-flex h-12 w-12 items-center justify-center rounded-xl text-white mb-6" style={{ background: card.accent }}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="relative font-display font-semibold text-medis-navy text-[16.5px] leading-snug">{card.title}</h3>
                <p className="relative mt-2.5 text-[13px] text-medis-gray leading-relaxed">{card.text}</p>
                <div
                  className="relative mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1"
                  style={{ color: card.accent }}
                >
                  Learn more
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
